import React from 'react';
import { FaStar, FaArchive, FaRedo } from 'react-icons/fa';
import { Badge } from '@/components/UI';

interface ParameterPreferenceControlsProps {
    viewMode: 'all' | 'pinned';
    onViewModeChange: (mode: 'all' | 'pinned') => void;
    showArchived: boolean;
    onShowArchivedChange: (show: boolean) => void;
    pinnedCount: number;
    archivedCount: number;
    onReset?: () => void;
}

const ParameterPreferenceControls: React.FC<ParameterPreferenceControlsProps> = ({
    viewMode,
    onViewModeChange,
    showArchived,
    onShowArchivedChange,
    pinnedCount,
    archivedCount,
    onReset
}) => {
    return (
        <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
                <span className="text-zinc-400 text-sm">View:</span>
                <div className="flex rounded-lg bg-zinc-800 p-0.5">
                    <button
                        className={`
                            px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            ${viewMode === 'all' ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}
                        `}
                        onClick={() => onViewModeChange('all')}
                    >
                        All Parameters
                    </button>
                    <button
                        className={`
                            px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                            flex items-center gap-1.5
                            ${viewMode === 'pinned' ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}
                        `}
                        disabled={pinnedCount === 0}
                        onClick={() => onViewModeChange('pinned')}
                    >
                        <FaStar className="text-yellow-400" size={12} />
                        Pinned Only
                        {pinnedCount > 0 && (
                            <Badge className="ml-1" variant="secondary">
                                {pinnedCount}
                            </Badge>
                        )}
                    </button>
                </div>
            </div>

            {/* Show Archived Toggle */}
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        checked={showArchived}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        type="checkbox"
                        onChange={(e) => onShowArchivedChange(e.target.checked)}
                    />
                    <span className="text-zinc-400 text-sm flex items-center gap-1.5">
                        <FaArchive size={12} />
                        Show Archived
                        {archivedCount > 0 && <Badge variant="secondary">{archivedCount}</Badge>}
                    </span>
                </label>
            </div>

            {/* Reset Button */}
            {onReset && (pinnedCount > 0 || archivedCount > 0) && (
                <button
                    className="
                        px-3 py-1.5 rounded-md text-sm font-medium
                        text-zinc-400 hover:text-white hover:bg-zinc-700
                        transition-all duration-200 flex items-center gap-1.5
                    "
                    title="Reset all preferences to default"
                    onClick={onReset}
                >
                    <FaRedo size={12} />
                    Reset All
                </button>
            )}
        </div>
    );
};

export default ParameterPreferenceControls;
